import {
	ArrayMinSize,
	ArrayUnique,
	IsArray,
	IsBoolean,
	IsEnum,
	IsInt,
	IsNotEmpty,
	IsNumber,
	IsObject,
	IsOptional,
	IsString,
	IsUrl,
	IsUUID,
	Min,
	ValidateIf,
	ValidateNested,
} from 'class-validator';
import {
	ALLOWED_EVENT_AVATAR_MIME_TYPES,
	Base64AllowedMimeTypes,
	CreateScheduleRepeat,
	DiscountTypeEnum,
	EventInstanceDto,
	EventPolicyTypesEnum,
	FileUploadDto,
	IsAfter,
	MaxBase64FileSize,
	PrivacyTypesEnum,
	ScheduleRepeatTypesEnum,
	shiftScheduleRepeat,
	UpdateEventToFitnessTypeDto,
	defaultEventDuration,
	CreateEventPriceDto,
	EditEventSponsorDto,
	EventAdditionalData,
	IntakeFormReservedFieldNames,
	EventPriceTypesEnum,
	IsHigherThan,
} from '@sweatpals/shared';
import { Type } from 'class-transformer';
import { compact, isNumber, map, sortBy } from 'lodash-es';
import { addHours } from 'date-fns';
import { PlacesAutocompleteOption, UsersAutocompleteOption } from '../../interfaces';
import {
	AttendeeRegistrationFieldTypeEnum,
	EventPriceToMembershipType,
	EventTypeEnum,
	EventWaiverTypeEnum,
} from '../../constants';
import { DiscountFormDto, DiscountTierEnum } from './discounts.dto';
import {
	AttendeeRegistrationFieldFormDto,
	AttendeeRegistrationFormDto,
	TiersTypeEnum,
} from './attendee-registration-form.dto';
import { WaiverFormDto } from './waiver-form.dto';

export class CreateEventPriceToMembershipDto {
	@IsEnum(EventPriceToMembershipType)
	@IsNotEmpty()
	public type: EventPriceToMembershipType;

	@IsUUID()
	@IsNotEmpty()
	public membershipId: string;

	@IsInt()
	@IsNotEmpty()
	public priceAmount: number;

	@IsEnum(DiscountTypeEnum)
	@IsNotEmpty()
	public discountType: DiscountTypeEnum;
}

export class CreateEventTierDto {
	@IsOptional()
	@IsUUID()
	public id?: string;

	@IsNumber()
	@IsInt()
	@IsNotEmpty({ message: 'Enter a ticket price' })
	public priceAmount: number;

	@IsString()
	@IsNotEmpty({ message: 'Enter a tier name' })
	public tierName: string;

	@IsEnum(EventPriceTypesEnum)
	@IsNotEmpty()
	public tierTypeId: EventPriceTypesEnum;

	@ValidateIf((o: CreateEventTierDto) => o.tierTypeId === EventPriceTypesEnum.Group)
	@IsNumber()
	@IsInt()
	@Min(2)
	@IsNotEmpty()
	public ticketsPerGroup?: number | null;

	@IsBoolean()
	public withTicketsLimitPerOrder: boolean;

	@ValidateIf((o: CreateEventTierDto) => o.withTicketsLimitPerOrder)
	@IsNumber()
	@IsInt()
	@Min(1)
	@IsNotEmpty()
	public minTicketsPerOrder?: number | null;

	@ValidateIf((o: CreateEventTierDto) => o.withTicketsLimitPerOrder)
	@IsNumber()
	@IsInt()
	@IsNotEmpty()
	@IsHigherThan('minTicketsPerOrder')
	public maxTicketsPerOrder?: number | null;

	@IsOptional()
	@IsString()
	public currency?: string;

	@IsOptional()
	@IsString()
	public tierDescription?: string;

	@IsOptional()
	@IsBoolean()
	public isBaseTier?: boolean;

	@IsOptional()
	@IsBoolean()
	public withMaxTickets?: boolean;

	@ValidateIf(o => o.withMaxTickets)
	@IsNotEmpty()
	@IsNumber()
	public maxTickets: number | null;

	@IsNotEmpty()
	@IsUUID()
	public key: string;

	@IsOptional()
	@IsBoolean()
	public withMemberships?: boolean;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => CreateEventPriceToMembershipDto)
	public eventPricesToMemberships?: CreateEventPriceToMembershipDto[];

	@IsOptional()
	@IsInt()
	@Min(0)
	public order: number;
}

export class PricesFormDto {
	@IsNotEmpty()
	@IsEnum(EventTypeEnum)
	public type: EventTypeEnum;

	@ValidateIf((o) => o.type === EventTypeEnum.Paid)
	@IsArray()
	@ArrayMinSize(1)
	@ArrayUnique((d) => d.tierName)
	@ValidateNested({ each: true })
	@Type(() => CreateEventTierDto)
	public tiers?: CreateEventTierDto[];

	@ValidateIf((o) => o.type === EventTypeEnum.Paid)
	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@ArrayUnique((d) => d.code)
	@Type(() => DiscountFormDto)
	public discounts?: DiscountFormDto[];

	@IsOptional()
	@IsNumber({ allowNaN: false }, { message: 'Minimal donation amount should be a number' })
	@IsInt({ message: 'Minimal donation amount should be an integer' })
	@Min(0, { message: 'Minimal donation amount should be zero or more' })
	public minimalDonationAmount?: number;

	@IsOptional()
	@IsBoolean()
	public withAttendeesLimit?: boolean;

	@ValidateIf((o) => o.withAttendeesLimit)
	@IsNotEmpty()
	@Min(1, { message: 'Event Capacity must not be less than 1' })
	public attendeesLimit: number | null;
}

export class EventFormDto extends PricesFormDto {
	@IsString()
	@IsNotEmpty({ message: 'Enter an event name' })
	public name: string;

	@IsString()
	@IsNotEmpty({ message: 'Select host' })
	public authorId?: string;

	@ValidateIf((o) => o.withCohost)
	@IsNotEmpty({ message: 'Select cohost' })
	public cohost?: UsersAutocompleteOption;

	@IsOptional()
	@IsArray()
	public sponsors?: EditEventSponsorDto[];

	@IsOptional()
	@IsString()
	public avatarId?: string;

	@ValidateIf((o) => !o.isOnlineEvent)
	@IsNotEmpty({ message: 'Please enter a valid address' })
	public address?: PlacesAutocompleteOption;

	@ValidateIf((o) => o.isOnlineEvent)
	@IsUrl({}, { message: 'Event link should be a valid' })
	public onlineUrl?: string;

	@IsAfter('now', { message: 'Start date can not before now.' })
	@IsNotEmpty({ message: 'Select start date' })
	public startDate: Date | null;

	@IsAfter('startDate', { message: 'End date can not before start date.' })
	@IsAfter('now', { message: 'End date can not before now.' })
	@IsNotEmpty({ message: 'Select end date' })
	public endDate: Date | null;

	@IsString()
	@IsNotEmpty()
	public tzid: string;

	@IsEnum(PrivacyTypesEnum)
	@IsNotEmpty()
	public privacyId: PrivacyTypesEnum;

	@IsOptional()
	@IsString()
	public description?: string;

	@IsOptional()
	@IsString()
	public subHeaderMarkdown?: string;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => UpdateEventToFitnessTypeDto)
	public fitnessTypes?: UpdateEventToFitnessTypeDto[];

	@IsOptional()
	@IsBoolean()
	public isRsvpListHidden?: boolean;

	@IsOptional()
	@IsBoolean()
	public withWaiver?: boolean;

	@IsOptional()
	public waiver?: WaiverFormDto;

	@IsOptional()
	@IsBoolean()
	public withChatroom?: boolean;

	@IsOptional()
	@IsBoolean()
	public withEventBundle?: boolean;

	@IsOptional()
	public eventBundleIds?: string[];

	@IsOptional()
	@IsBoolean()
	public isWaitlistEnabled?: boolean;

	@IsOptional()
	@IsBoolean()
	public isOnlineEvent?: boolean;

	@IsOptional()
	@IsBoolean()
	public withCohost?: boolean;

	@IsOptional()
	@IsBoolean()
	public withQuestionnaire?: boolean;

	@IsOptional()
	@IsBoolean()
	public withSalesStartDate?: boolean;

	@IsOptional()
	@IsNotEmpty({ message: 'Select sales start date' })
	public salesStartDate?: Date;

	@IsOptional()
	public scheduleRepeat?: CreateScheduleRepeat;

	@IsOptional()
	@ValidateNested()
	@Type(() => AttendeeRegistrationFormDto)
	@IsObject()
	public attendeeRegistration?: AttendeeRegistrationFormDto;

	@IsNotEmpty()
	@IsEnum(EventPolicyTypesEnum)
	public policyType: EventPolicyTypesEnum;

	@IsOptional()
	@ValidateNested()
	@Type(() => FileUploadDto)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_EVENT_AVATAR_MIME_TYPES)
	public avatarImg?: FileUploadDto;

	@IsOptional()
	@IsBoolean()
	public isCommunityChatSharingEnabled?: boolean;

	public additionalData?: EventAdditionalData;

	// eslint-disable-next-line complexity
	public static fromEvent(event: EventInstanceDto, isEditMode = false): EventFormDto {
		const { intakeFormSettings } = event;

		const type = event.isDonationBased ? EventTypeEnum.Donation : event.isPaid ? EventTypeEnum.Paid : EventTypeEnum.Free;

		const withEventBundle = !!event.eventBundles?.length;
		const withWaiver = !!event.waiverUrl || !!intakeFormSettings?.waiverText;
		const withChatroom = !!event.eventChatCid;
		// filter sections which have questions (except reserved fields)
		const withQuestionnaire = !!intakeFormSettings?.sections?.filter(
			s => s.fields?.filter(f => !f.reserved && !Object.values(IntakeFormReservedFieldNames).includes(f.name as IntakeFormReservedFieldNames))?.length
		)?.length;
		const withSalesStartDate = !!event?.additionalData?.ticketSalesStartUtcTimeIso;
		const salesStartDate = withSalesStartDate && event?.additionalData?.ticketSalesStartUtcTimeIso ? new Date(event.additionalData.ticketSalesStartUtcTimeIso) : undefined;

		const eventBundleIds = event.eventBundles?.map(bundle => bundle?.id)?.filter(id => !!id) ?? [];

		const startDate = event?.instance || event.startDate
			? new Date(event?.instance ?? event.startDate)
			: addHours(new Date(), 24);
		const endDate = event?.instanceEndDate || event.endDate
			? new Date(event?.instanceEndDate || event.endDate!)
			: defaultEventDuration;

		const data: EventFormDto = {
			name: event.name,
			authorId: event.authorId,
			avatarId: event.avatarId,
			attendeesLimit: event.attendeesLimit ?? null,
			withAttendeesLimit: isNumber(event.attendeesLimit),
			type,
			onlineUrl: event.onlineUrl,
			startDate: new Date(startDate),
			endDate: new Date(endDate),
			tzid: event.tzid,
			privacyId: event.privacyId,
			description: event.description,
			subHeaderMarkdown: event.subHeaderMarkdown,
			fitnessTypes: event.fitnessTypes,
			isRsvpListHidden: event.isRsvpListHidden,
			withWaiver,
			withChatroom,
			withQuestionnaire,
			withEventBundle,
			eventBundleIds,
			isOnlineEvent: event.isOnlineEvent,
			policyType: event.policyType,
			sponsors: event.eventSponsors,
			withSalesStartDate,
			salesStartDate,
			minimalDonationAmount: event.minimalDonationAmount,
			isWaitlistEnabled: event.isWaitlistEnabled,
			additionalData: {
				...(event.additionalData ?? {}),
			},
		};

		if (withWaiver) {
			data.waiver = {
				waiverType: intakeFormSettings?.waiverText ? EventWaiverTypeEnum.Text : EventWaiverTypeEnum.Url,
				waiverUrl: event.waiverUrl,
				waiverText: intakeFormSettings?.waiverText,
			};
		}

		const attendeeRegistrationFields = (intakeFormSettings?.sections?.[0]?.fields ?? [])
			.filter((f) => !f.reserved)
			.map<AttendeeRegistrationFieldFormDto>((field) => ({
				...field,
				question: (field.type === 'check-box' ? field.checkboxLabel : field.label) ?? '',
				type: field.type as AttendeeRegistrationFieldTypeEnum,
				required: field.required ?? false,
				tiersType: field.tierIds ? TiersTypeEnum.Selected : TiersTypeEnum.All,
			}));

		if (
			attendeeRegistrationFields.length ||
			intakeFormSettings?.fullNameReservedField ||
			intakeFormSettings?.phoneReservedField ||
			intakeFormSettings?.emailReservedField ||
			intakeFormSettings?.showSectionsOnceForMultipleTickets
		) {
			data.attendeeRegistration = {
				...(data.attendeeRegistration ?? {}),
				isReservedFieldsRequires: true,
				fullNameReservedField: intakeFormSettings?.fullNameReservedField ?? { enabled: false },
				phoneReservedField: intakeFormSettings?.phoneReservedField ?? { enabled: false },
				emailReservedField: intakeFormSettings?.emailReservedField ?? { enabled: false },
				fields: attendeeRegistrationFields,
				showSectionsOnceForMultipleTickets: intakeFormSettings?.showSectionsOnceForMultipleTickets,
			};
		}

		if (event?.scheduleRepeat) {
			const schedule = event.scheduleRepeat;
			data.scheduleRepeat = {
				typeId: schedule.typeId,
				weekOfMonth: schedule.weekOfMonth,
				dayOfMonth: schedule.dayOfMonth,
				interval: schedule.interval,
				dayOfWeekIds: schedule.daysOfWeek?.map(({ id }) => id),
				endDate: schedule.endDate,
			};

			if (event.startDate) {
				data.scheduleRepeat = shiftScheduleRepeat(new Date(event.startDate), data.scheduleRepeat, true, event.tzid);
			}
		} else {
			data.scheduleRepeat = { typeId: ScheduleRepeatTypesEnum.Never };
		}

		if (event.prices?.length) {
			data.tiers = sortBy(event.prices, 'order').map((price) => ({
				id: isEditMode ? price.id : undefined,
				priceAmount: price.priceAmount,
				tierName: price.tierName,
				currency: price.currency,
				tierDescription: price.tierDescription,
				tierTypeId: price.tierTypeId,
				ticketsPerGroup: price.ticketsPerGroup,
				withTicketsLimitPerOrder: isNumber(price.minTicketsPerOrder) && isNumber(price.maxTicketsPerOrder),
				minTicketsPerOrder: price.minTicketsPerOrder,
				maxTicketsPerOrder: price.maxTicketsPerOrder,
				isBaseTier: price.isBaseTier,
				withMaxTickets: isNumber(price.maxTickets),
				maxTickets: price.maxTickets ?? null,
				key: price.id,
				withMemberships: !!price.eventPriceToMemberships?.length,
				// Convert discount to ticket price for membership
				eventPricesToMemberships: price.eventPriceToMemberships?.map(item => ({
					priceAmount:
						item.type === DiscountTypeEnum.Fixed
							? Math.max(price.priceAmount - item.amount, 0)
							: Math.max(100 - item.amount, 0),
					discountType: item.type,
					membershipId: item.membershipId,
					type:
						item.type === DiscountTypeEnum.Fixed
							? EventPriceToMembershipType.CustomPrice
							: item.amount === 100
							? EventPriceToMembershipType.Free
							: item.amount === 50
							? EventPriceToMembershipType.HalfPrice
							: item.amount === 0
							? EventPriceToMembershipType.FullPrice
							: EventPriceToMembershipType.CustomPrice,
				})),
				order: price.order,
			}));
		}

		if (event.discounts?.length) {
			const tiers = event.prices ?? [];
			data.discounts = event.discounts?.map((discount) => {
				const tierIds = compact(map(discount.discountToEvents ?? [], 'eventPriceId'));
				const filteredTierIds = map(
					tiers.filter(({ id }) => tierIds.includes(id)),
					'id'
				);

				return {
					id: isEditMode ? discount.id : undefined,
					amount: discount.amount,
					type: discount.type,
					code: discount.code,
					withMaxUses: isNumber(discount.maxUses),
					maxUses: discount.maxUses ?? null,
					tiersType: filteredTierIds.length ? DiscountTierEnum.Selected : DiscountTierEnum.All,
					tiers: filteredTierIds.length
						? filteredTierIds.reduce((acc, id) => ({ ...acc, [id]: true }), {})
						: { all: true },
				};
			});
		}

		return data;
	}
}

export class CreatePriceTierDto extends CreateEventPriceDto {
	public id?: string;
}

export class CreateEventFormData extends EventFormDto {
	@IsOptional()
	@ValidateIf((o) => !!o.authorId)
	@IsUUID()
	@IsNotEmpty({ message: 'Select host' })
	public override authorId?: string;

	public allowToPublishToOtherPlatforms?: boolean;

	public addressValue?: string;
}


export class EditOngoingEventFormData extends CreateEventFormData {
	@IsNotEmpty({ message: 'Select start date' })
	public override startDate: Date | null;

	@IsAfter('startDate', { message: 'End date can not before start date.' })
	@IsNotEmpty({ message: 'Select end date' })
	public override endDate: Date | null;
}
